/**
 * Pure transcript-patch application (HIG-5964/R4b).
 *
 * The list-level operations here are the single source of truth for how a
 * `StorePatch` mutates a chat's message list — `useMessagesStore`'s hook
 * callbacks delegate to them one patch at a time, and the engine sink's
 * frame flush folds a whole buffer through `applyTranscriptPatches` so many
 * SSE events become ONE `chatMessagesAtom` write instead of a full-record
 * rewrite per event. No React, no jotai — testable with literals.
 */
import type { StorePatch } from '../engine/accumulator'
import type { AssistantMessage, ChatMessage, ToolCall } from '../types'
import {
  adoptAnyAssistantPlaceholder,
  adoptAssistantPlaceholder,
  releaseAssistantRow,
} from '../utils/message-reconciliation'

export type ChatMessagesRecord = Record<string, Array<ChatMessage>>

/** Ops that mutate the transcript record (vs. the loader/metadata/thinking channels). */
export type TranscriptPatch = Extract<
  StorePatch,
  {
    op:
      | 'addMessage'
      | 'upsertMessage'
      | 'adoptPlaceholder'
      | 'updateLastMessage'
      | 'discardMessage'
      | 'addToolCall'
      | 'updateToolCall'
  }
>

export function isTranscriptPatch(patch: StorePatch): patch is TranscriptPatch {
  switch (patch.op) {
    case 'addMessage':
    case 'upsertMessage':
    case 'adoptPlaceholder':
    case 'updateLastMessage':
    case 'discardMessage':
    case 'addToolCall':
    case 'updateToolCall':
      return true
    default:
      return false
  }
}

/** Merge by id, or append when the id is new. */
export function upsertMessageInList(messages: Array<ChatMessage>, message: ChatMessage): Array<ChatMessage> {
  const existingIndex = messages.findIndex((existing) => existing.id === message.id)
  if (existingIndex === -1) {
    return [...messages, message]
  }
  const updated = [...messages]
  updated[existingIndex] = { ...updated[existingIndex], ...message }
  return updated
}

/**
 * Merge into the message matched by id, falling back to the most recent
 * message with the same role. A patch that names a `clientId` targets that
 * client-minted message alone — never a neighbor: the turn's rename must not
 * land on the previous user bubble when the turn itself put no bubble in the
 * transcript (a promptless continuation). Returns the input list unchanged
 * (same reference) when nothing matches.
 */
export function updateLastMessageInList(
  messages: Array<ChatMessage>,
  message: Partial<ChatMessage>,
): Array<ChatMessage> {
  let index = messages.findIndex((m) => m.id === message.id)
  if (index === -1 && message.clientId !== undefined) {
    index = messages.findIndex((m) => m.clientId === message.clientId)
    if (index === -1) {
      return messages
    }
  }
  if (index === -1) {
    // Fallback: the most recent message with the same role.
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === message.role) {
        index = i
        break
      }
    }
  }
  if (index === -1) {
    return messages
  }
  const updated = [...messages]
  updated[index] = { ...updated[index], ...message } as ChatMessage
  return updated
}

/**
 * Add a tool call to the assistant message that owns it, merging by tool-call
 * id when it already exists. Returns the input list unchanged (same
 * reference) when the message isn't found.
 */
export function addToolCallInList(
  messages: Array<ChatMessage>,
  messageId: string,
  toolCall: ToolCall,
): Array<ChatMessage> {
  const index = messages.findIndex((m) => m.id === messageId && m.role === 'assistant')
  if (index === -1) {
    return messages
  }
  const assistantMessage = messages[index] as AssistantMessage
  const existingToolCalls = assistantMessage.toolCalls || []
  const existingToolCallIndex = existingToolCalls.findIndex((existing) => existing.id === toolCall.id)

  const toolCalls =
    existingToolCallIndex === -1
      ? [...existingToolCalls, toolCall]
      : existingToolCalls.map((existing, i) => (i === existingToolCallIndex ? { ...existing, ...toolCall } : existing))

  const updated = [...messages]
  updated[index] = { ...assistantMessage, toolCalls }
  return updated
}

/**
 * Whether a tool call flipping to success was a skill mutation — the signal
 * `GlobalEvents.emit('skills:changed')` listeners refresh on.
 */
function isSkillsMutation(toolCall: ToolCall, updates: Partial<ToolCall>): boolean {
  if (updates.status !== 'success' || toolCall.status === 'success') {
    return false
  }
  const outerName = (toolCall.toolName ?? '').toLowerCase()
  const input = (toolCall.toolInput ?? {}) as Record<string, unknown>
  const innerName = typeof input.name === 'string' ? input.name.toLowerCase() : ''
  const inputAction = typeof input.action === 'string' ? input.action.toLowerCase() : ''
  const name = innerName || outerName
  // `add` is here for `add_skill_preference`. Without it an open Settings page
  // keeps rendering its cached copy of the skill.
  const mutationPattern = /create|update|delete|fork|edit|save|patch|remove|add/
  // Path 1: toolSet/other wrapper — input.name is the inner tool name e.g. manage_skills_update_skill
  // Path 2: direct manage_skill tool — outerName contains "skill", input.action has the verb
  return (
    (name.includes('skill') && mutationPattern.test(name)) ||
    (outerName.includes('skill') && mutationPattern.test(inputAction))
  )
}

/**
 * Merge updates into a tool call, matched by `toolCallId` alone. Returns the
 * input list unchanged (same reference) when the tool call isn't found.
 */
export function updateToolCallInList(
  messages: Array<ChatMessage>,
  toolCallId: string,
  updates: Partial<ToolCall>,
): { messages: Array<ChatMessage>; skillsChanged: boolean } {
  let skillsChanged = false
  const index = messages.findIndex(
    (m) => m.role === 'assistant' && (m as AssistantMessage).toolCalls?.some((tc) => tc.id === toolCallId),
  )
  if (index === -1) {
    return { messages, skillsChanged }
  }
  const assistantMessage = messages[index] as AssistantMessage
  const toolCalls = (assistantMessage.toolCalls || []).map((toolCall) => {
    if (toolCall.id !== toolCallId) return toolCall
    if (isSkillsMutation(toolCall, updates)) {
      skillsChanged = true
    }
    return { ...toolCall, ...updates }
  })
  const updated = [...messages]
  updated[index] = { ...assistantMessage, toolCalls }
  return { messages: updated, skillsChanged }
}

function withChatList(record: ChatMessagesRecord, chatId: string, messages: Array<ChatMessage>): ChatMessagesRecord {
  if (record[chatId] === messages) return record
  return { ...record, [chatId]: messages }
}

/**
 * Fold a buffer of transcript patches into the record — one new record for
 * the whole buffer, in emission order. `skillsChanged` reports whether any
 * tool-call update was a skill mutation (the caller emits the global event).
 */
export function applyTranscriptPatches(
  record: ChatMessagesRecord,
  patches: ReadonlyArray<TranscriptPatch>,
): { record: ChatMessagesRecord; skillsChanged: boolean } {
  let next = record
  let skillsChanged = false

  for (const patch of patches) {
    const messages = next[patch.chatId] ?? []
    switch (patch.op) {
      case 'addMessage':
        // New assistant messages adopt the chat's unadopted optimistic
        // placeholder when one exists — a resume turn can race a submit
        // before that submit's placeholder was adopted (see use-engine-deps).
        next = withChatList(
          next,
          patch.chatId,
          patch.message.role === 'assistant'
            ? adoptAnyAssistantPlaceholder(messages, patch.message as AssistantMessage)
            : [...messages, patch.message],
        )
        break
      case 'upsertMessage':
        next = withChatList(next, patch.chatId, upsertMessageInList(messages, patch.message))
        break
      case 'adoptPlaceholder':
        next = withChatList(next, patch.chatId, adoptAssistantPlaceholder(messages, patch.clientId, patch.message))
        break
      case 'updateLastMessage': {
        // No list yet: seed the chat with the partial, matching the legacy
        // store behavior for optimistic first writes.
        const updated =
          next[patch.chatId] === undefined
            ? [patch.message as ChatMessage]
            : updateLastMessageInList(messages, patch.message)
        next = withChatList(next, patch.chatId, updated)
        break
      }
      case 'discardMessage':
        next = withChatList(next, patch.chatId, releaseAssistantRow(messages, patch.messageId))
        break
      case 'addToolCall':
        next = withChatList(next, patch.chatId, addToolCallInList(messages, patch.messageId, patch.toolCall))
        break
      case 'updateToolCall': {
        const result = updateToolCallInList(messages, patch.toolCallId, patch.updates)
        skillsChanged = skillsChanged || result.skillsChanged
        next = withChatList(next, patch.chatId, result.messages)
        break
      }
    }
  }

  return { record: next, skillsChanged }
}
